'use client';

import { X } from 'lucide-react';
import { useState } from 'react';
import Button from '../shared/Button';
import { AdminService } from '../../services';
import { Toast } from '../shared/Toast';
import Tooltip from '../shared/Tooltip';

export default function ChangePasswordModal({ isOpen, onClose, onChangePassword }) {
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showOld, setShowOld] = useState(false);
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false); // Add isLoading state

	const handleClose = () => {
		setOldPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setError('');
		onClose();
	};

	const InfoTooltip = () => (
		<span className="ml-1 inline-flex items-center justify-center w-[12px] h-[12px] rounded-full border border-[#180A63] text-[#180A63] text-[10px] font-semibold leading-none">
			?
		</span>
	);

	if (!isOpen) return null;

	const handleCancelClick = () => {
		setOldPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setError('');
		if (onClose) {
			onClose(); // Close the modal
		}
	};

	const handleBackdropMouseDown = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		if (!oldPassword) {
			Toast.error('Please enter your current password.');
			setIsLoading(false);
			return;
		}
		if (oldPassword.length < 8) {
			Toast.error('Current password must be at least 8 characters long.');
			setIsLoading(false);
			return;
		}

		if (!newPassword) {
			Toast.error('Please create a new password.');
			setIsLoading(false);
			return;
		}
		if (newPassword.length < 8) {
			Toast.error('New password must be at least 8 characters long.');
			setIsLoading(false);
			return;
		}

		if (!passwordRegex.test(newPassword)) {
			Toast.error('Use a mix of letters, numbers, and symbols for a stronger password.');
			setIsLoading(false);
			return;
		}

		if (!confirmPassword.trim()) {
			Toast.error('Please confirm your new password.');
			setIsLoading(false);
			return;
		}

		if (newPassword.trim() !== confirmPassword.trim()) {
			Toast.error('Passwords don’t match. Try again.');
			setIsLoading(false);
			return;
		}

		if (oldPassword.trim() === newPassword.trim()) {
			Toast.error('New password cannot be the same as the current password.');
			setIsLoading(false);
			return;
		}

		try {
			const response = await AdminService.changePassword(oldPassword, newPassword);

			if (response.status === 200) {
				setOldPassword('');
				setNewPassword('');
				setConfirmPassword('');
				// On successful password change, close this modal
				onClose();
				// Call the onChangePassword prop to notify the parent component
				if (onChangePassword) {
					onChangePassword(response.message || 'Password changed successfully');
				}
			}
		} catch (error) {
			console.error('Change password error:', error);
			// Check if the error is from an API response
			if (error.response) {
				// Extract specific error message from the API response
				const message = error.response.data?.message || 'An API error occurred.';
				const status = error.response.status;
				const lowerMsg = String(message).toLowerCase();
				if (
					status === 400 ||
					status === 401 ||
					lowerMsg.includes('old password') ||
					lowerMsg.includes('current password')
				) {
					Toast.error(message);
					setError('');
				} else {
					setError(message);
				}
			} else {
				// Generic network error for actual network failures
				Toast.error('Network error. Please check your connection and try again.');
			}
		} finally {
			setIsLoading(false); // Reset loading state
		}
	};

	// Removed isPasswordMatched usage; direct comparisons are used in validation

	// 👁 Eye Open SVG
	const EyeOpen = (
		<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
			<g opacity="0.5">
				<path
					d="M10.9948 5.17676C14.577 5.17684 17.6093 7.53095 18.6285 10.7764C17.6094 14.0219 14.5771 16.3769 10.9948 16.377C7.41235 16.377 4.38016 14.022 3.36096 10.7764C4.38027 7.53095 7.41248 5.17676 10.9948 5.17676ZM10.9948 7.57715C10.1462 7.57715 9.33214 7.91374 8.73206 8.51367C8.13203 9.1137 7.79466 9.92782 7.79456 10.7764C7.79456 11.625 8.132 12.439 8.73206 13.0391C9.33217 13.6392 10.1461 13.9766 10.9948 13.9766C11.8434 13.9765 12.6574 13.6391 13.2574 13.0391C13.8574 12.439 14.1949 11.6249 14.1949 10.7764C14.1948 9.92782 13.8575 9.1137 13.2574 8.51367C12.6574 7.91366 11.8433 7.5772 10.9948 7.57715ZM10.9948 9.17676C11.419 9.17678 11.8256 9.34556 12.1256 9.64551C12.4256 9.94547 12.5943 10.3522 12.5944 10.7764C12.5944 11.2007 12.4257 11.6081 12.1256 11.9082C11.8256 12.2081 11.4189 12.3769 10.9948 12.377C10.5704 12.377 10.163 12.2083 9.86292 11.9082C9.56295 11.6082 9.39514 11.2006 9.39514 10.7764C9.39525 10.3523 9.5631 9.94545 9.86292 9.64551C10.163 9.34545 10.5704 9.17676 10.9948 9.17676Z"
					fill="#180A63"
				/>
			</g>
		</svg>
	);

	// 👁‍🗨 Eye Closed SVG
	const EyeClosed = (
		<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
			<g opacity="0.5">
				<path
					d="M18.2783 17.1426L17.2178 18.2031L3.0752 4.06055L4.13574 3L18.2783 17.1426ZM7.54785 9.5332C7.47307 9.80619 7.43363 10.09 7.43359 10.3779C7.43359 11.2266 7.77104 12.0405 8.37109 12.6406C8.97121 13.2407 9.7851 13.5781 10.6338 13.5781C10.9215 13.5781 11.2048 13.5376 11.4775 13.4629L13.4736 15.459C12.591 15.7943 11.634 15.9785 10.6338 15.9785C7.05139 15.9785 4.0192 13.6235 3 10.3779C3.40221 9.09733 4.11847 7.95616 5.05762 7.04297L7.54785 9.5332ZM10.6338 4.77832C14.216 4.7784 17.2483 7.13251 18.2676 10.3779C17.8481 11.7139 17.0866 12.898 16.0869 13.8301L13.6631 11.4062C13.7746 11.078 13.834 10.7311 13.834 10.3779C13.8339 9.52938 13.4965 8.71526 12.8965 8.11523C12.2964 7.51523 11.4824 7.17876 10.6338 7.17871C10.2807 7.17871 9.93369 7.23735 9.60547 7.34863L7.62305 5.36621C8.55258 4.9883 9.5685 4.77832 10.6338 4.77832ZM9.66309 11.6484C9.60698 11.6056 9.55242 11.5602 9.50195 11.5098C9.45149 11.4593 9.40609 11.4048 9.36328 11.3486L9.66309 11.6484ZM11.1074 8.85059C11.3532 8.9269 11.5793 9.06177 11.7646 9.24707C11.9497 9.43209 12.0838 9.65802 12.1602 9.90332L11.1074 8.85059Z"
					fill="#180A63"
				/>
			</g>
		</svg>
	);

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
			onMouseDown={handleBackdropMouseDown}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/70" />

			{/* Modal */}
			<div
				className="relative z-10 w-full max-w-[500px] rounded-[30px] bg-white shadow-2xl p-[50px]"
				onMouseDown={(e) => e.stopPropagation()}
			>
				{/* Close button */}
				<button
					onClick={handleClose}
					className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>
				{/* Heading */}
				<div className="flex items-center">
					<h2 className="text-xl sm:text-2xl font-bold text-dark-blue mb-3">Change Password</h2>
					<Tooltip content="Change the password for your WriteForm account.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<p className="text-gray text-sm sm:text-base mb-[50px]">
					Change your WriteForm account password
				</p>
				{error && (
					<div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
						{error}
					</div>
				)}
				<div className="mb-[60px]">
					{/* Old Password */}
					<div className="mb-6">
						<div className="flex items-center gap-2 mb-2">
							<label htmlFor="old-password" className="text-sm font-medium text-dark-blue">
								Current Password <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter your current password.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<div className="relative">
							<input
								id="old-password"
								type={showOld ? 'text' : 'password'}
								minLength={8}
								value={oldPassword}
								onChange={(e) => setOldPassword(e.target.value)}
								className="w-full rounded-[10px] border border-gray px-4 py-3 pr-10 text-sm outline-none"
								placeholder="••••••••••••"
							/>
							<div
								className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
								onClick={() => setShowOld((prev) => !prev)}
							>
								{showOld ? EyeOpen : EyeClosed}
							</div>
						</div>
					</div>

					{/* New Password */}
					<div className="mb-6">
						<label htmlFor="new-password" className="block text-sm font-medium text-dark-blue mb-2">
							New Password <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								id="new-password"
								minLength={8}
								type={showNew ? 'text' : 'password'}
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								className="w-full rounded-[10px] border border-gray px-4 py-3 pr-10 text-sm outline-none"
								placeholder="••••••••••••"
							/>
							<div
								className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
								onClick={() => setShowNew((prev) => !prev)}
							>
								{showNew ? EyeOpen : EyeClosed}
							</div>
						</div>
						<p className="text-[12px] text-gray mt-2">
							Use at least 8 characters with letters, numbers, and symbols.
						</p>
					</div>

					{/* Confirm Password */}
					<div className="mb-6">
						<label
							htmlFor="confirm-password"
							className="block text-sm font-medium text-dark-blue mb-2"
						>
							Confirm Password <span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								id="confirm-password"
								minLength={8}
								type={showConfirm ? 'text' : 'password'}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className="w-full rounded-[10px] border border-gray px-4 py-3 pr-10 text-sm outline-none"
								placeholder="••••••••••••"
							/>
							<div
								className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
								onClick={() => setShowConfirm((prev) => !prev)}
							>
								{showConfirm ? EyeOpen : EyeClosed}
							</div>
						</div>
						{/* {confirmPassword && (
							<p
								className={`text-[12px] mt-2 ${
									isPasswordMatched ? 'text-[#108E56]' : 'text-red-500'
								}`}
							>
								{isPasswordMatched ? 'Passwords matched' : 'Passwords do not match'}
							</p>
						)} */}
					</div>
				</div>
				{/* Buttons */}
				<div className="flex gap-3">
					<Button
						onClick={handleCancelClick}
						variant="outline-gradient-text"
						className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-sm sm:text-base"
					>
						Cancel
					</Button>
					{/* ✅ Correct – tooltip wraps the button itself */}
					<Tooltip content="Update your WriteForm account password.">
						<Button
							onClick={handleChangePassword}
							className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-white text-sm sm:text-base"
						>
							{isLoading ? 'Changing...' : 'Change Password'}
						</Button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}
